<template>
  <v-container ref="container" v-scroll="onScroll">
    <TitleParagraphRow :value="value" />

    <v-row>
      <v-col cols="12">
        <v-row
          v-if="isMounted && !hideFilters"
          :style="{ top: `${stickyTop}px` }"
          class="sticky-lg"
          style="z-index: 1"
        >
          <v-col v-if="filtersData">
            <v-card elevation="4" rounded="0" theme="light">
              <v-card-text>
                <v-row align="center">
                  <v-col>
                    <v-card
                      class="text-h4 font-weight-light"
                      variant="flat"
                      @click="toggleFilters"
                    >
                      <v-icon
                        :icon="mdiTuneVariant"
                        color="surface-variant"
                        size="42"
                      />
                      Filters
                    </v-card></v-col
                  >

                  <v-col cols="auto">
                    <v-btn variant="text" @click="resetFilters"
                      >Reset Filters</v-btn
                    >
                    <v-btn
                      :append-icon="
                        filtersShown ? mdiChevronUp : mdiChevronDown
                      "
                      variant="text"
                      @click="toggleFilters"
                      >{{ filtersShown ? 'Hide' : 'Show' }}
                    </v-btn>
                  </v-col>
                </v-row>
              </v-card-text>

              <v-expand-transition>
                <v-card-text v-if="filtersShown">
                  <v-row class="pt-2">
                    <v-col
                      v-if="shownFilters.what && (whatMenu || whatProducts)"
                      cols="12"
                      md="6"
                      xl=""
                    >
                      <WhatSelect
                        ref="whatSelect"
                        :what-items="whatItems"
                        :what-menu="whatMenu"
                        :what-products="whatProducts"
                        @select="selectWhatItem"
                      />
                    </v-col>

                    <v-col
                      v-if="shownFilters.deliveryMethod"
                      cols="12"
                      md="6"
                      xl=""
                    >
                      <v-select
                        v-model="filtersData.deliveryMethod"
                        :items="deliveryMethodItems"
                        hide-details
                        label="Format"
                        variant="outlined"
                      />
                    </v-col>

                    <v-col v-if="shownFilters.who" cols="12" md="6" xl="">
                      <v-select
                        v-model="filtersData.who"
                        :items="trainerItems"
                        hide-details
                        label="Trainer"
                        variant="outlined"
                      />
                    </v-col>

                    <v-col v-if="shownFilters.when" cols="12" md="6" xl="">
                      <v-select
                        v-model="filtersData.when"
                        :items="whenItems"
                        :menu-props="{ maxHeight: 500 }"
                        hide-details
                        label="When"
                        variant="outlined"
                      />
                    </v-col>
                    <!-- TODO: Custom range -->
                  </v-row>
                </v-card-text>
              </v-expand-transition>
            </v-card>
          </v-col>
        </v-row>

        <v-row v-if="banner && banner.status === 'published'" justify="center">
          <v-col cols="12" xl="8">
            <BannerCard :value="banner" />
          </v-col>
        </v-row>

        <OccasionList
          v-if="filtersData"
          :filters-value="filtersData"
          :hide-info-button="hideInfoButton"
          :limit="!isMounted ? undefined : main ? 10 : 5"
          :show-all-button="main"
          :show-embed-code="showEmbedCode"
        />
      </v-col>
    </v-row>

    <v-row v-if="!main" class="pt-6">
      <v-col align="center">
        <v-btn
          :append-icon="mdiArrowRight"
          :href="fullScheduleHref"
          color="secondary"
          variant="text"
        >
          View Full Schedule
        </v-btn>
      </v-col>
    </v-row>
  </v-container>
</template>

<script setup>
import { computed, onMounted, ref, watch } from 'vue'
import { useDisplay } from 'vuetify'
import { useIntervalFn, useMounted } from '@vueuse/core'
import { DateTime } from 'luxon'
import {
  APP_BAR_HEIGHT,
  FILTER_DELIVERY_METHOD_ALL,
  FILTER_WHAT_ALL,
  FILTER_WHEN_HALF_DAY,
  FILTER_WHEN_UPCOMING,
  FILTER_WHEN_WEEKEND,
  FILTER_WHO_ALL
} from '#root/lib/consts'
import { scrollToRef } from '#root/lib/utils'
import { useDeliveryMethodsStore } from '#root/lib/useDeliveryMethodsStore'
import { useOccasionsStore } from '#root/lib/useOccasionsStore'
import { useTrainerPersonsStore } from '#root/lib/useTrainerPersonsStore'
import debounce from 'lodash.debounce'
import {
  mdiArrowRight,
  mdiChevronDown,
  mdiChevronUp,
  mdiTuneVariant
} from '@mdi/js'

const FILTERS_DEFAULT = {
  deliveryMethod: true,
  what: true,
  when: true,
  who: true
}
const FILTERS_VALUE_DEFAULT = {
  deliveryMethod: FILTER_DELIVERY_METHOD_ALL,
  what: FILTER_WHAT_ALL,
  when: FILTER_WHEN_UPCOMING,
  who: FILTER_WHO_ALL
}
const REFETCH_INTERVAL = 20 * 60 * 1000 // 20m

const emit = defineEmits(['update:filtersValue'])

const props = defineProps({
  banner: {
    default: undefined,
    type: Object
  },
  hideFilters: {
    default: false,
    type: Boolean
  },
  hideInfoButton: {
    default: false,
    type: Boolean
  },
  filters: {
    default: undefined,
    type: Object
  },
  filtersValue: {
    default: undefined,
    type: Object
  },
  fullScheduleHref: {
    default: '/training/schedule',
    type: String
  },
  main: {
    default: false,
    type: Boolean
  },
  showEmbedCode: {
    default: false,
    type: Boolean
  },
  showPast: {
    default: false,
    type: Boolean
  },
  stickyTop: {
    default: APP_BAR_HEIGHT,
    type: Number
  },
  value: {
    required: true,
    type: Object
  },
  whatItems: {
    default: undefined,
    type: Array
  },
  whatMenu: {
    default: undefined,
    type: Object
  },
  whatProducts: {
    default: undefined,
    type: Array
  }
})

const { lgAndUp } = useDisplay()
const { items: deliveryMethods } = useDeliveryMethodsStore()
const {
  refetchData: refetchOccasions,
  showPast: showPastOccasions,
  yearMonthIndex
} = useOccasionsStore()
const { items: trainers } = useTrainerPersonsStore()
const isMounted = useMounted()
const container = ref(null)
const filtersData = ref(null)
const filtersShown = ref(true)
const filtersAutoHideEnabled = ref(true)
const filtersOriginal = ref(null)
const shownFilters = computed(() => {
  return Object.assign({}, FILTERS_DEFAULT, props.filters)
})

const deliveryMethodItems = computed(() => {
  return [
    { title: 'All Formats', value: FILTER_DELIVERY_METHOD_ALL },
    ...(!deliveryMethods.value
      ? []
      : deliveryMethods.value.map(({ name, code }) => ({
          title: name,
          value: code
        })))
  ]
})
const trainerItems = computed(() => {
  return [
    { title: 'All Trainers', value: FILTER_WHO_ALL },
    ...(!trainers.value
      ? []
      : trainers.value.map(({ name, code }) => ({
          title: name,
          value: code
        })))
  ]
})
const whatSelect = ref(null)
const whenItems = computed(() => {
  return [
    {
      title: props.showPast ? 'Upcoming and Past' : 'Upcoming',
      value: FILTER_WHEN_UPCOMING
    },
    {
      title: 'Half Day',
      value: FILTER_WHEN_HALF_DAY
    },
    {
      title: 'Weekend',
      value: FILTER_WHEN_WEEKEND
    },
    ...Object.keys(yearMonthIndex.value).map(month => {
      return {
        title: DateTime.fromISO(month).toFormat('MMMM y'),
        value: month
      }
    })
  ]
})

/** @type {function | undefined} */
let debounced

function onScroll(e) {
  if (
    lgAndUp.value &&
    filtersAutoHideEnabled.value &&
    filtersShown.value &&
    e.target.documentElement.scrollTop > window.innerHeight / 2
  )
    filtersShown.value = false
}

function resetFilters() {
  filtersData.value = Object.assign({}, filtersOriginal.value)

  if (whatSelect.value && filtersData.value)
    whatSelect.value.selectItem(filtersData.value.what)
}

function selectWhatItem(newWhat) {
  filtersData.value.what = newWhat
}

function toggleFilters() {
  if (typeof debounced === 'function') debounced()

  filtersAutoHideEnabled.value = false
  filtersShown.value = !filtersShown.value
}

useIntervalFn(async () => refetchOccasions(), REFETCH_INTERVAL)

onMounted(() => {
  watch(
    filtersData,
    newValue => {
      if (!debounced) {
        debounced = debounce(() => {
          filtersAutoHideEnabled.value = true
        }, 2000)
      } else if (container.value && lgAndUp.value) {
        debounced()
        filtersAutoHideEnabled.value = false

        scrollToRef(container, { behavior: 'instant' })
      }

      emit('update:filtersValue', newValue)
    },
    { deep: true }
  )

  watch(whatSelect, newValue => {
    if (newValue) newValue.selectItem(filtersData.value.what)
  })

  filtersData.value = Object.assign(
    {},
    FILTERS_VALUE_DEFAULT,
    props.filtersValue
  )
  filtersOriginal.value = Object.assign({}, filtersData.value)

  showPastOccasions.value = props.showPast
})
</script>
